/* TASK-3314 — Preserve vertical scroll position across page refresh (F5)
   and site-language change (the header flag toggle).

   Problem: on a plain reload, and especially on a language switch (the flag
   in custom-header-bar navigates via `window.location.href` to the SAME path
   under a different locale prefix — see custom-overrides.js `computeSwap()`),
   the visitor is dumped back at the very top of the page.

   Approach (theme-standard, sessionStorage based):
     • On every page leave (pagehide / beforeunload / tab hidden) we snapshot
       the current scrollY together with the full path and a locale-stripped
       "base" path.
     • On the next load we restore that scrollY when either
         (a) this is a RELOAD of the exact same URL  → F5 / refresh, or
         (b) this is a NAVIGATE whose base path matches but whose full path
             differs → only the locale prefix changed → language switch.
       Any other navigation (a genuinely different page, back/forward to
       another page) is left untouched, so fresh visits still open at the top.
     • Content length differs between languages, so we restore the PIXEL
       position (clamped to the new document height), not a specific element —
       this is the accepted, "approximately the same place" behaviour asked
       for. The restore is re-applied a few times over ~1.2s so late-growing
       height (lazy images / async sections) does not clamp us short.

   scrollRestoration is intentionally left at the browser default ('auto') so
   native back/forward + bfcache restoration keep working; we only *add*
   handling for the reload and language-switch cases the browser misses. */
(function () {
  'use strict';

  var KEY = 'ILS_SCROLL_RESTORE_V1';      // per-leave snapshot (refresh)
  var LANG_KEY = 'ILS_SCROLL_LANG_V1';    // one-shot marker set on flag click
  var FRESH_MS = 60000; // ignore snapshots older than 60s

  // Locale root, e.g. '/' or '/en-eu/'. Shopify exposes this on every
  // storefront; fall back to a generic /xx-yy prefix parse.
  function localeRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
    } catch (e) {}
    return '/';
  }

  // Strip the active locale prefix so the same page in two languages maps to
  // one stable "base" key.
  function baseOf(path) {
    var root = localeRoot();
    if (root && root !== '/' && path.indexOf(root.replace(/\/$/, '')) === 0) {
      var rest = path.slice(root.replace(/\/$/, '').length);
      return rest || '/';
    }
    // Generic fallback: drop a leading /xx or /xx-yy locale segment.
    return path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/, '') || '/';
  }

  var curFull = location.pathname;
  var curBase = baseOf(curFull);

  function scrollYNow() {
    return window.scrollY || window.pageYOffset ||
      (document.documentElement && document.documentElement.scrollTop) || 0;
  }

  function save() {
    try {
      var y = scrollYNow();
      sessionStorage.setItem(KEY, JSON.stringify({
        full: curFull, base: curBase, y: y, t: Date.now()
      }));
    } catch (e) {}
  }

  window.addEventListener('pagehide', save);
  window.addEventListener('beforeunload', save);
  document.addEventListener('visibilitychange', function () {
    // iOS Safari sometimes skips pagehide; hidden is the reliable last hook.
    if (document.visibilityState === 'hidden') save();
  });

  // Language switch: the header flag ([data-ils-lang], bound in
  // custom-overrides.js) navigates via window.location.href to the same page
  // under a new locale prefix. Because collection/product/page handles are
  // LOCALISED (e.g. /en-eu/collections/all → /ru-eu/collections/vse), the
  // destination path is not predictable from the source — so we drop an
  // explicit one-shot marker at click time (capture phase, before the button's
  // own handler fires) and honour it on the very next load regardless of path.
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-ils-lang]') : null;
    if (!el) return;
    try {
      sessionStorage.setItem(LANG_KEY, JSON.stringify({ y: scrollYNow(), t: Date.now() }));
    } catch (err) {}
  }, true);

  function navType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type; // 'reload' | 'navigate' | 'back_forward'
    } catch (e) {}
    try {
      // Legacy API: 1 === TYPE_RELOAD
      if (performance.navigation) return performance.navigation.type === 1 ? 'reload' : 'navigate';
    } catch (e) {}
    return 'navigate';
  }

  function readJSON(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }

  function restore() {
    // TASK-4997 — an explicit #track-order deep link wins over the remembered
    // pixel offset: the visitor asked for a specific block, not for wherever
    // they happened to be last time. Consume the one-shot language-switch
    // marker anyway so it cannot leak into a later navigation.
    // TASK-5015 added #ils-ds-check (the order checker on the delayed-delivery
    // page) to the same allow-list: that hash exists on that one page only, so
    // extending the guard here cannot affect anything else, and it stops this
    // file dragging such a deep link back to the remembered offset.
    var explicitHash = (location.hash || '').toLowerCase();
    if (explicitHash === '#track-order' || explicitHash === '#ils-ds-check') {
      try { sessionStorage.removeItem(LANG_KEY); } catch (e) {}
      return;
    }

    var t = navType();
    var target = null;

    // (1) One-shot language-switch marker wins — path is unpredictable because
    // handles are localised, so we trust the explicit marker over any path
    // comparison. Consume it so it never leaks into a later navigation.
    if (t !== 'reload') {
      var lang = readJSON(LANG_KEY);
      try { sessionStorage.removeItem(LANG_KEY); } catch (e) {}
      if (lang && typeof lang.y === 'number' && lang.y > 0 && (Date.now() - (lang.t || 0)) <= FRESH_MS) {
        target = lang.y;
      }
    }

    // (2) Otherwise fall back to the per-leave snapshot for a same-URL reload,
    // or (rarely) a locale change where the base path DID stay identical.
    if (target === null) {
      var d = readJSON(KEY);
      if (!d || typeof d.y !== 'number' || d.y <= 0) return;
      if ((Date.now() - (d.t || 0)) > FRESH_MS) return;
      var isReload = (t === 'reload' && d.full === curFull);
      var isLangSwitch = (t !== 'back_forward' && d.base === curBase && d.full !== curFull);
      if (!isReload && !isLangSwitch) return;
      target = d.y;
    }

    function apply() {
      var max = Math.max(
        0,
        (document.documentElement ? document.documentElement.scrollHeight : 0),
        (document.body ? document.body.scrollHeight : 0)
      ) - window.innerHeight;
      var y = Math.min(target, Math.max(0, max));
      window.scrollTo(0, y);
    }

    // Fire now and re-assert as layout settles / lazy content grows height.
    apply();
    try { requestAnimationFrame(apply); } catch (e) {}
    var delays = [30, 90, 200, 450, 800, 1200];
    for (var i = 0; i < delays.length; i++) {
      setTimeout(apply, delays[i]);
    }
  }

  if (document.readyState === 'complete') {
    restore();
  } else {
    document.addEventListener('DOMContentLoaded', restore);
    window.addEventListener('load', restore);
  }
})();
